/**
 * @file index.tsx
 *
 * @fileoverview Component that maps directly to CSS grid properties using styled-system. Based on grid-layout for
 * styled-system https://styled-system.com/api/#grid-layout
 */

import { ReactNode } from "react";
import {
  AlignItemsProps,
  JustifyContentProps,
  JustifyItemsProps,
} from "../../../../theme/layout/itemsLayout";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../theme/spacing";
import { getScopedClasses } from "../../../../utils/responsive";
import styles from "./grid.module.scss";

export type GridColumnsProp = 1 | 2 | 3 | 4 | 5;

type GridAutoFlowProps =
  | "-moz-initial"
  | "inherit"
  | "initial"
  | "revert"
  | "unset"
  | "column"
  | "dense"
  | "row";

type TextAlignProps =
  | "center"
  | "end"
  | "justify"
  | "left"
  | "match-parent"
  | "right"
  | "start";

export type GridProps = SpaceProps & {
  children?: ReactNode;
  /**
   * Responsive grid gap property.
   */
  gap?: number | number[];
  /**
   * The number of columns to show.
   */
  columns?: GridColumnsProp | GridColumnsProp[] | string;
  /**
   * Grid template columns (if responsive string).
   */
  gridTemplateColumns?: string;
  /**
   * Grid template rows (if responsive string).
   */
  gridTemplateRows?: string;
  /**
   * Responsive grid autoflow props.
   */
  gridAutoFlow?: GridAutoFlowProps | GridAutoFlowProps[];
  /**
   * Responsive width property.
   */
  width?: number | number[];
  /**
   * Responsive height property.
   */
  height?: number | number[];
  /**
   * Responsive align items prop.
   */
  alignItems?: AlignItemsProps | AlignItemsProps[];
  /**
   * Responsive justify items prop.
   */
  justifyItems?: JustifyItemsProps | JustifyItemsProps[];
  /**
   * Responsive justify content prop.
   */
  justifyContent?: JustifyContentProps | JustifyContentProps[];
  /**
   * Responsive text align props.
   */
  textAlign?: TextAlignProps | TextAlignProps[];
  /**
   * className prop to allow overriding styles from outside.
   */
  className?: string;
};

/**
 * A Grid allows for displaying information in columns and rows next to each other.
 * All properties can accept an array for responsive values. Inside Grids there should be "Cell" components which also have
 * specific properties for mapping to CSS Grid properties.
 */
export const Grid = ({
  children,
  gap = 6,
  columns,
  gridTemplateColumns,
  gridTemplateRows,
  gridAutoFlow,
  width,
  height,
  alignItems,
  justifyItems,
  justifyContent,
  textAlign,
  className = "",
  ...props
}: GridProps) => {
  return (
    <div
      className={`
        ${className}
        ${styles.grid}
        ${generateMarginClasses(props, styles)}
        ${generatePaddingClasses(props, styles)}
        ${getScopedClasses(styles, "grid-gap", gap)}
        ${getScopedClasses(styles, "grid-columns", columns)}
        ${getScopedClasses(styles, "grid-af", gridAutoFlow)}
        ${getScopedClasses(styles, "grid-width", width)}
        ${getScopedClasses(styles, "grid-height", height)}
        ${getScopedClasses(styles, "ai", alignItems)}
        ${getScopedClasses(styles, "ji", justifyItems)}
        ${getScopedClasses(styles, "jc", justifyContent)}
        ${getScopedClasses(styles, "grid-ta", textAlign)}
      `.trim()}
      style={{
        ...(typeof columns === "string"
          ? { gridTemplateColumns: columns } // For responsive grid columns (e.g columns={'repeat(auto-fit,minmax(200px,1fr))'})
          : null),
        ...(gridTemplateColumns && { gridTemplateColumns }),
        ...(gridTemplateRows && { gridTemplateRows }),
      }}
      {...excludeSpacingProps(props)}
    >
      {children}
    </div>
  );
};
export default Grid;
